'use client';

import dynamic from 'next/dynamic';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle
} from '@/components/ui/dialog';
import { guidanceVideoAssets } from '@/lib/guidance/areas';
import { stopPlayback } from '@/lib/guidance/video-playback';

// Loaded when the dialog first opens, so a page pays nothing for the player.
const GuidanceVideoPlayer = dynamic(() => import('./guidance-video-player'), {
  ssr: false
});

interface GuidanceVideoDialogProps {
  /** A folder under `public/guidance/videos/`. */
  videoId: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A page's narrated guidance video, in a window. Opened from the stage banner's
 * thumbnail and, once the banner has been seen, from the page's Help button
 * (VALE-2138), so both open the same thing. The player mounts only while open.
 */
export function GuidanceVideoDialog({
  videoId,
  title,
  open,
  onOpenChange
}: GuidanceVideoDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Stop the sound the moment the dialog closes, before the player
        // unmounts (VALE-2067).
        if (!next) stopPlayback();
        onOpenChange(next);
      }}
    >
      <DialogContent
        // A window, after the reference the owner picked: a header strip
        // carrying the title and the close, and the video full-bleed under
        // it with no padding. As large as the screen allows without
        // fullscreen: 94% of the width, or whatever width keeps the 16:9
        // video plus the header (~8rem with the margin) inside the viewport
        // height, whichever is smaller.
        hideCloseButton
        className="bg-card w-[min(94vw,calc((100dvh_-_8rem)*16/9))] max-w-none grid-cols-[minmax(0,1fr)] gap-0 overflow-hidden p-0 sm:rounded-xl"
      >
        <div className="border-border flex items-center justify-between gap-4 border-b py-2 pr-2 pl-4">
          <DialogTitle className="text-foreground min-w-0 truncate text-sm font-medium">
            {title}
          </DialogTitle>
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              tone="muted"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DialogClose>
        </div>
        {open && (
          <GuidanceVideoPlayer
            videoId={videoId}
            assets={guidanceVideoAssets(videoId)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
